import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
// import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice"; // Store Custom Code
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import StoreProductQtyBreakPricing from "@store/Components/Store/StoreProductQtyBreakPricing";
import ProductPrice, { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import { StoreHasEnoughInventory } from "@store/Store/Data/Products/ProductsSelector";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
/* Store Custom Code - End */

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
}

/* Store Custom Code - Start */
const mapStateToProps = (state: StoreApplicationState, props: HasProductContext) => ({
    productSettings: getSettingsCollection(state).productSettings,
    session: state.context.session,
    hasEnoughInventory: StoreHasEnoughInventory(state, props.productContext),
});
/* Store Custom Code - End */

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    // addToCartWrapper?: InjectableCss; // Store Custom Code
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    // Store Custom Code
    addToCartWrapper?: GridContainerProps;
    priceGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    actionsGridItemHidden?: GridItemProps;
    priceSkeleton?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 0; // Store Custom Code
            `,
        },
    },
    addToCartWrapper: {
        gap: 0, // Store Custom Code
        css: css`
            display: flex;
            align-items: flex-end;
            /* Store Custom Code - Start */
            justify-content: center;

            &.section-shown {
                margin-top: 20px;
            }
            /* Store Custom Code - End */
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 4.1rem;

                /* Store Custom Code - Start */
                input {
                    border-width: 2px !important;
                    text-align: center;
                }
                /* Store Custom Code - End */
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
        `,
    },
    // Store Custom Code
    priceGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: space-between;
            flex-wrap: wrap;
            padding: 12px 0;
            border-top: 1px solid ${COLORS.border.base};
            border-bottom: 1px solid ${COLORS.border.base};
        `,
    },
    price: {
        wrapper: {
            css: css`
                width: auto;
            `,
        },
        price: {
            priceText: {
                weight: TYPOGRAPHY.weight.medium,
                color: COLORS.secondary.base,
                size: TYPOGRAPHY.size.large,
            },
            errorText: { weight: TYPOGRAPHY.weight.medium },
        },
    },
    actionsGridItem: {
        width: 12,
        css: css`
            padding: 20px 0;
            min-height: 80px;
        `,
    },
    actionsGridItemHidden: {
        width: [12, 12, 12, 12, 12],
        css: css`
            min-height: 80px;
            padding: 20px 0;
        `,
    },
    priceSkeleton: {
        css: css`
            display: flex;
            height: 100%;
            margin-right: 10%;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    // Store Custom Code
    session,
    hasEnoughInventory,
}) => {
    /* Store Custom Code - Start */
    const hidePrice = productContext.product?.properties?.hidePrice;
    const hasBreakPricing =
        productContext.productInfo.pricing?.unitRegularBreakPrices &&
        productContext.productInfo.pricing?.unitRegularBreakPrices.length > 1;

    const renderPrices = () => {
        if (!showPrice || hidePrice) {
            return;
        }

        if (session.isAuthenticated) {
            return (
                <GridItem {...styles.priceGridItem}>
                    {hasBreakPricing ? (
                        <StoreProductQtyBreakPricing />
                    ) : (
                        <ProductPrice
                            product={productContext}
                            showLabel={false}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.price}
                            showMSRPPrices={true}
                            hidePriceIfBreakPrices={true}
                        />
                    )}
                    <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
                </GridItem>
            );
        }
    };

    const sectionShown =
        session.isAuthenticated ||
        !productContext.product.properties.hidePrice ||
        (productSettings.canAddToCart && hasEnoughInventory);
    /* Store Custom Code - End */

    return (
        <StyledWrapper {...styles.wrapper}>
            {/* Store Custom Code - Start */}
            <GridContainer {...styles.addToCartWrapper} className={sectionShown ? "section-shown" : ""}>
                {renderPrices()}
                <GridItem {...styles.actionsGridItem}>
                    {productSettings.canAddToCart && hasEnoughInventory && (
                        <>
                            <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                            <ProductAddToCartButton
                                data-test-selector={`actionsAddToCart${productContext.product.id}`}
                                extendedStyles={styles.addToCartButton}
                            />
                        </>
                    )}
                </GridItem>
            </GridContainer>
            {/* Store Custom Code - End */}
            {showAddToList && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListActions)); // Store Custom Code
